import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { api, errMsg, fromISO, prettyDate, toISO } from "@/lib/api";
import { Panel } from "@/components/admin/Panel";

const REASONS = ["Maintenance", "Personal use", "Booked elsewhere", "Other"];

const expand = (start, endExclusive) => {
  const out = [];
  for (let d = fromISO(start); toISO(d) < endExclusive; d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)) out.push(new Date(d));
  return out;
};
const nightsInRange = (r) => (r?.from ? expand(toISO(r.from), toISO(new Date(+(r.to || r.from) + 864e5))) : []);

const Legend = () => (
  <div className="flex flex-wrap gap-4 text-xs mb-4">
    <span className="flex items-center gap-1.5"><i className="w-3 h-3 rounded-sm rdp-day_blocked inline-block" /> Owner block</span>
    <span className="flex items-center gap-1.5"><i className="w-3 h-3 rounded-sm rdp-day_booked inline-block" /> Confirmed reservation</span>
    <span className="flex items-center gap-1.5"><i className="w-3 h-3 rounded-sm rdp-day_pending inline-block" /> Pending checkout</span>
    <span className="flex items-center gap-1.5"><i className="w-3 h-3 rounded-sm rdp-day_airbnb inline-block" /> Airbnb (imported)</span>
  </div>
);

export const CalendarAdjustments = () => {
  const [data, setData] = useState(null);
  const [range, setRange] = useState();
  const [reason, setReason] = useState(REASONS[0]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [months, setMonths] = useState(window.innerWidth >= 1024 ? 2 : 1);
  const load = () => api.get("/admin/calendar").then((r) => setData(r.data)).catch(() => toast.error("Could not load calendar"));
  useEffect(() => { load(); setMonths(window.innerWidth >= 1024 ? 2 : 1); }, []);
  if (!data) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const byNight = {};
  data.bookings.forEach((b) => expand(b.check_in, b.check_out).forEach((d) => { byNight[toISO(d)] = b; }));
  const blockMap = Object.fromEntries(data.blocks.map((b) => [b.date, b]));
  const sel = nightsInRange(range).map(toISO);
  const selBookings = [...new Set(sel.map((d) => byNight[d]).filter(Boolean))];
  const submit = async (blocked) => {
    setBusy(true);
    try {
      await api.post("/admin/blocks", { dates: sel, blocked, reason, note });
      toast.success(blocked ? `Blocked ${sel.length} night(s)` : "Dates made available");
      setRange(undefined); setNote(""); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const nights = (s) => data.bookings.filter((b) => b.status === s).flatMap((b) => expand(b.check_in, b.check_out));
  return (
    <Panel title="Calendar Adjustments" desc="Select a night or a range of nights, then block or unblock them. Guest reservations can't be changed here." testid="calendar-panel">
      <Legend />
      <div className="grid lg:grid-cols-[auto_1fr] gap-8">
        <div className="overflow-x-auto" data-testid="admin-calendar">
          <Calendar mode="range" selected={range} onSelect={setRange} numberOfMonths={months} disabled={{ before: new Date() }}
            modifiers={{ blocked: data.blocks.map((b) => fromISO(b.date)), booked: nights("confirmed"), pending: nights("pending"), airbnb: data.ical.flatMap((e) => expand(e.start, e.end)) }}
            modifiersClassNames={{ blocked: "rdp-day_blocked", booked: "rdp-day_booked", pending: "rdp-day_pending", airbnb: "rdp-day_airbnb" }} />
        </div>
        <div className="space-y-4" data-testid="calendar-selection-panel">
          {!sel.length ? <p className="text-sm text-muted-foreground">Click a start night, then an end night.</p> : (
            <>
              <p className="text-sm"><strong>{sel.length} night(s)</strong>: {prettyDate(sel[0])}{sel.length > 1 && ` to ${prettyDate(sel[sel.length - 1])}`}</p>
              {selBookings.map((b) => <p key={b.id} className="text-sm border border-border rounded p-2 bg-[hsl(96_14%_94%)]" data-testid="selection-booking-info">Reservation {b.ref} ({b.status}): {b.name}, {b.guests} guest(s), {prettyDate(b.check_in)} → {prettyDate(b.check_out)}</p>)}
              {sel.filter((d) => blockMap[d]).slice(0, 3).map((d) => <p key={d} className="text-xs text-muted-foreground">{prettyDate(d)} blocked: {blockMap[d].reason}{blockMap[d].note && ` · ${blockMap[d].note}`}</p>)}
              <select value={reason} onChange={(e) => setReason(e.target.value)} className="border border-input rounded-md h-10 px-3 text-sm bg-white w-full" data-testid="block-reason-select">{REASONS.map((r) => <option key={r}>{r}</option>)}</select>
              <Input placeholder="Internal note (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="block-note-input" />
              <div className="flex flex-wrap gap-3">
                <button onClick={() => submit(true)} disabled={busy} className="bg-primary text-primary-foreground px-5 py-2 rounded-full text-sm disabled:opacity-60" data-testid="block-dates-button">Mark unavailable</button>
                <button onClick={() => submit(false)} disabled={busy} className="border border-border px-5 py-2 rounded-full text-sm disabled:opacity-60" data-testid="unblock-dates-button">Make available</button>
                <button onClick={() => setRange(undefined)} className="text-sm underline" data-testid="clear-selection-button">Clear</button>
              </div>
            </>
          )}
          <div className="pt-4 border-t border-border">
            <p className="text-sm font-semibold mb-2">Owner blocks</p>
            {!data.blocks.length ? <p className="text-xs text-muted-foreground">No blocked dates.</p> : (
              <ul className="text-xs space-y-1 max-h-48 overflow-y-auto" data-testid="owner-blocks-list">{data.blocks.map((b) => <li key={b.date}>{prettyDate(b.date)} · {b.reason}{b.note && ` · ${b.note}`}</li>)}</ul>
            )}
          </div>
        </div>
      </div>
    </Panel>
  );
};
